import { _isNil, _notNil } from '@/littledash';
import type { ColumnSettings, GridSettings } from '@/support/hot.types';
import type { components } from '@/generated/internal-api/openapi-types';
import {
  buildTreatmentNameValidator,
  findTreatmentForRow,
  NAME_PROPERTY,
  type TreatmentTypeEditRow,
} from '@/utils/treatmentTypeValidation';

type TreatmentTypeGlossary = components['schemas']['TreatmentTypeGlossaryV1'];

export const ARCHIVED_TREATMENT_TOOLTIP = 'This treatment is archived and cannot be modified';

/** Row property holding the `api_id` that ties each row to its treatment. */
export const API_ID_PROPERTY = 'api_id';

/** Column index the hidden `api_id` property is rendered at. */
export const API_ID_COLUMN = 1;

interface BuildColumnsParams {
  treatments: Array<TreatmentTypeGlossary>;
}

/**
 * Column definitions for the bulk edit grid. The validator is given the treatments being edited
 * so a name left unchanged is accepted without a uniqueness request against itself. The second
 * column carries each row's `api_id` and is hidden — it exists so the save payload identifies
 * treatments by identity rather than by row position.
 */
export const buildColumns = ({ treatments }: BuildColumnsParams): Array<ColumnSettings> => [
  {
    data: NAME_PROPERTY,
    type: 'text',
    className: 'htMiddle',
    validator: buildTreatmentNameValidator({ treatments }),
  },
  {
    data: API_ID_PROPERTY,
    type: 'text',
    readOnly: true,
  },
];

export const initBulkEditTreatmentsSettings = (columns: Array<ColumnSettings>): GridSettings => ({
  columns,
  colHeaders: ['Treatment name', 'Treatment id'],
  hiddenColumns: { columns: [API_ID_COLUMN], copyPasteEnabled: false },
  rowHeaders: true,
  manualColumnResize: true,
  contextMenu: false as const,
  outsideClickDeselects: false,
  height: 600,
  rowHeights: 50,
  className: 'htMiddle',
  stretchH: 'all' as const,
});

/**
 * Makes archived rows read-only. Archived treatments are reachable here because the glossary's
 * status filter allows them to be selected alongside active ones.
 *
 * The treatment is resolved through the row's hidden `api_id` rather than its index. Reading it
 * from `rows` rather than from the grid matters: this callback runs inside `getCellMeta`, so
 * calling back into the instance would re-enter it.
 */
export const buildCellsCallback = (treatments: Array<TreatmentTypeGlossary>, rows: Array<TreatmentTypeEditRow>) => {
  return (row: number, col: number): Record<string, unknown> => {
    if (col === API_ID_COLUMN) {
      return { readOnly: true };
    }
    const treatment = findTreatmentForRow(treatments, rows[row]?.[API_ID_PROPERTY]);
    if (_notNil(treatment) && _notNil(treatment.archived_at)) {
      return { readOnly: true, className: 'htMiddle readOnly' };
    }
    return {};
  };
};

/** Explains via tooltip why a read-only cell cannot be edited. */
export const buildAfterRendererCallback = (
  treatments: Array<TreatmentTypeGlossary>,
  rows: Array<TreatmentTypeEditRow>
) => {
  return (
    td: HTMLTableCellElement,
    row: number,
    _col: number,
    _prop: string | number,
    _value: unknown,
    cellProperties: object
  ): void => {
    const { readOnly } = cellProperties as { readOnly?: boolean };
    if (readOnly !== true || _isNil(findTreatmentForRow(treatments, rows[row]?.[API_ID_PROPERTY]))) {
      return;
    }

    td.setAttribute('data-tooltip-id', 'global-tooltip-id');
    td.setAttribute('data-tooltip-content', ARCHIVED_TREATMENT_TOOLTIP);
    td.setAttribute('data-type', 'info');
  };
};
